import React, { useState, useEffect } from 'react'
import { Edit, Menu } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import Breadcrumb from '../../components/Common/Breadcrumb'
import HelpModal from '../../components/HelpModal/HelpModal'
import submoduleService from '../../services/submoduleService'
import { toast } from 'react-toastify'

const SubmoduleMasterView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const submoduleData = location.state?.submoduleData || {}
  const id = submoduleData.id || submoduleData._id

  const [data, setData] = useState({
    submoduleCode: '',
    name: '',
    displayName: '',
    description: '',
    menuItem: null,
    type: 'Master',
    sortOrder: 0,
    isActive: true,
    isDisplay: true
  })
  const [loading, setLoading] = useState(!!id)
  const [showHelp, setShowHelp] = useState(false)

  useEffect(() => {
    const loadItem = async () => {
      if (!id) {
        setLoading(false)
        if (submoduleData.submoduleCode) {
          setData({
            submoduleCode: submoduleData.submoduleCode,
            name: submoduleData.name,
            displayName: submoduleData.displayName || '',
            description: submoduleData.description || '',
            menuItem: submoduleData.menuItem,
            type: submoduleData.type || 'Master',
            sortOrder: submoduleData.sortOrder ?? 0,
            isActive: submoduleData.isActive !== false,
            isDisplay: submoduleData.isDisplay !== false
          })
        }
        return
      }
      try {
        setLoading(true)
        const res = await submoduleService.getSubmoduleById(id)
        if (res?.success && res.data) {
          const d = res.data
          setData({
            submoduleCode: d.submoduleCode || '',
            name: d.name || '',
            displayName: d.displayName || '',
            description: d.description || '',
            menuItem: d.menuItem || null,
            type: d.type || 'Master',
            sortOrder: d.sortOrder ?? 0,
            isActive: d.isActive !== false,
            isDisplay: d.isDisplay !== false
          })
        }
      } catch (err) {
        toast.error('Failed to load module')
        navigate('/settings/submodule-master')
      } finally {
        setLoading(false)
      }
    }
    loadItem()
  }, [id])

  const breadcrumbItems = [
    { label: 'Settings', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Masters', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Module Management', path: '/settings/submodule-master', onClick: () => navigate('/settings/submodule-master') },
    { label: 'View Module', path: '', isCurrent: true }
  ]

  const helpContent = (
    <>
      <h3>View Module</h3>
      <p>Read-only view. Use Edit to make changes.</p>
    </>
  )

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} showBackButton={false} /></div>
        <div className="text-center py-5">Loading...</div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          showBackButton={false}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <Menu size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>View Module</h1>
          <p>{data.name || data.displayName || 'Module details'}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-primary" onClick={() => navigate('/settings/submodule-master/edit', { state: { submoduleData: { ...data, id: id || submoduleData.id, _id: id || submoduleData._id } } })}>
            <Edit size={18} />
            Edit
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Module Details</h2>
        </div>
        <div className="form-content view-mode" style={{ padding: '24px' }}>
          <div className="section-header">
            <h3>Module Information</h3>
          </div>
          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Module Code</label>
              <p className="form-value">{data.submoduleCode || '-'}</p>
            </div>
            <div className="form-group">
              <label className="form-label">Title</label>
              <p className="form-value">{data.name || '-'}</p>
            </div>
            <div className="form-group">
              <label className="form-label">Display Name</label>
              <p className="form-value">{data.displayName || data.name || '-'}</p>
            </div>
            <div className="form-group">
              <label className="form-label">Business Function</label>
              <p className="form-value">{data.menuItem?.label || '-'}</p>
            </div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <p className="form-value">{data.type || 'Master'}</p>
            </div>
            <div className="form-group">
              <label className="form-label">Order</label>
              <p className="form-value">{data.sortOrder ?? 0}</p>
            </div>
            <div className="form-group">
              <label className="form-label">Disabled</label>
              <p className="form-value"><span className={`status-badge ${data.isActive ? 'inactive' : 'active'}`}>{data.isActive ? 'No' : 'Yes'}</span></p>
            </div>
            <div className="form-group">
              <label className="form-label">Is Display</label>
              <p className="form-value"><span className={`status-badge ${data.isDisplay ? 'active' : 'inactive'}`}>{data.isDisplay ? 'Yes' : 'No'}</span></p>
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Description</label>
              <p className="form-value">{data.description || '-'}</p>
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/settings/submodule-master')}>Back to List</button>
          </div>
        </div>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="View Module – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default SubmoduleMasterView
